import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, LogIn, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const AdminLogin = () => {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSignUpMode, setIsSignUpMode] = useState(false);
  const [signUpData, setSignUpData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const navigate = useNavigate();

  // Check if already logged in
  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        // Check if user is admin
        const { data: userData, error } = await supabase
          .from('users')
          .select('account_type')
          .eq('id', data.session.user.id)
          .single();
        
        if (!error && userData && userData.account_type === 'admin') {
          navigate('/admin');
        }
      }
    };
    
    checkSession();
  }, [navigate]);

  const handleSignUpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSignUpData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!signUpData.firstName || !signUpData.lastName || !signUpData.email || !signUpData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (signUpData.password !== signUpData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (signUpData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Sign up with Supabase
      const { data: signUpResult, error: signUpError } = await supabase.auth.signUp({
        email: signUpData.email,
        password: signUpData.password,
        options: {
          emailRedirectTo: undefined,
          data: {
            first_name: signUpData.firstName,
            last_name: signUpData.lastName,
            account_type: 'admin'
          },
          // Disable email confirmation for admin signup
          captchaToken: undefined
        }
      });

      if (signUpError) {
        // Handle email confirmation errors gracefully
        if (signUpError.message.includes('confirmation email') || 
            signUpError.message.includes('Error sending confirmation email') ||
            signUpError.code === 'unexpected_failure') {
          console.warn('Email confirmation failed, attempting to create user anyway:', signUpError.message);
          
          // Try to create user record directly and attempt sign in
          try {
            // Generate a UUID for the user (this is a fallback approach)
            const tempUserId = crypto.randomUUID();
            
            // Create user record in users table
            const { error: userInsertError } = await supabase
              .from('users')
              .insert({
                id: tempUserId,
                first_name: signUpData.firstName,
                last_name: signUpData.lastName,
                email: signUpData.email,
                account_type: 'admin'
              });

            if (userInsertError) {
              console.warn('Direct user creation failed:', userInsertError);
              setError('Email service not configured. Please contact support or configure email settings in Supabase.');
              return;
            }

            setError('Admin account created, but email confirmation is disabled. Please contact support to enable your account, or configure email settings in your Supabase project.');
            return;
          } catch (fallbackError) {
            console.error('Fallback user creation failed:', fallbackError);
            setError('Email service not configured. Please configure SMTP settings in your Supabase project under Authentication → Settings.');
            return;
          }
        } else {
          throw signUpError;
        }
      }

      if (signUpResult.user) {
        // Create user record in users table
        const { error: userInsertError } = await supabase
          .from('users')
          .insert({
            id: signUpResult.user.id,
            first_name: signUpData.firstName,
            last_name: signUpData.lastName,
            email: signUpData.email,
            account_type: 'admin'
          });

        if (userInsertError) {
          console.warn('User record creation failed:', userInsertError);
        }

        // Try to sign in immediately
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email: signUpData.email,
          password: signUpData.password
        });

        if (signInError) {
          if (signInError.message.includes('Email not confirmed')) {
            setError('Admin account created successfully, but email confirmation is required. Please check your email or contact support to enable your account.');
          } else {
            throw signInError;
          }
        } else {
          // Success - redirect to admin dashboard
          navigate('/admin');
        }
      } else {
        setError('Failed to create admin account. Please try again.');
      }
    } catch (error) {
      console.error('Sign up error:', error);
      
      // Handle specific error cases
      if (error instanceof Error) {
        if (error.message.includes('23505') || error.message.includes('duplicate key value violates unique constraint')) {
          setError('An account with this email already exists. Please use a different email or try signing in instead.');
        } else if (error.message.includes('users_email_key')) {
          setError('This email is already registered. Please use a different email address.');
        } else {
          setError(error.message);
        }
      } else {
        setError('Failed to create admin account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle forgot password
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail) {
      setError('Please enter your email address');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Request password reset from Supabase
      const { error } = await supabase.auth.resetPasswordForEmail(forgotPasswordEmail, {
        redirectTo: `${window.location.origin}/admin/reset-password`,
      });
      
      if (error) throw error;
      
      setResetEmailSent(true);
    } catch (error) {
      console.error('Error requesting password reset:', error);
      setError('Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userId || !password) {
      setError('Please enter both user ID and password');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Use the standard signIn method from context
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: userId, // Treat userId as email for admin login
        password: password
      });

      if (signInError) {
        throw signInError;
      }
      
      // Check if user is admin
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('account_type')
        .eq('id', signInData.user.id)
        .single();
      
      if (userError || !userData || userData.account_type !== 'admin') {
        setError('Access denied. Admin privileges required.');
        return;
      }
      
      // Redirect to admin dashboard
      navigate('/admin/dashboard');
      
    } catch (error) {
      console.error('Login error:', error);
      setError('Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <img 
            src="/madeinbd-logo.png" 
            alt="Made in BD" 
            className="h-12 w-auto object-contain"
          />
        </Link>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          {isSignUpMode ? 'Create Admin Account' : 'Admin Login'}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {isSignUpMode 
            ? 'Create a new administrator account' 
            : forgotPasswordMode 
              ? 'Reset your admin password'
              : 'Sign in with your admin email and password'
          }
        </p>
        <div className="mt-4 text-center">
          {!forgotPasswordMode && (
            <button
              onClick={() => {
                setIsSignUpMode(!isSignUpMode);
                setError('');
                setUserId('');
                setPassword('');
                setSignUpData({
                  firstName: '',
                  lastName: '',
                  email: '',
                  password: '',
                  confirmPassword: ''
                });
              }}
              className="text-orange-600 hover:text-orange-500 text-sm font-medium"
            >
              {isSignUpMode ? 'Already have an account? Sign in' : 'Need to create an admin account? Sign up'}
            </button>
          )}
          {forgotPasswordMode && (
            <button
              onClick={() => {
                setForgotPasswordMode(false);
                setError('');
                setForgotPasswordEmail('');
                setResetEmailSent(false);
              }}
              className="text-orange-600 hover:text-orange-500 text-sm font-medium"
            >
              Back to login
            </button>
          )}
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <span>{error}</span>
            </div>
          )}
          
          {forgotPasswordMode ? (
            <form className="space-y-6" onSubmit={handleForgotPassword}>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Reset Your Password</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Enter your admin email address and we'll send you a link to reset your password.
                </p>
              </div>
              
              {/* Email */}
              <div>
                <label htmlFor="forgotPasswordEmail" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1 relative">
                  <input
                    id="forgotPasswordEmail"
                    name="forgotPasswordEmail"
                    type="email"
                    autoComplete="email"
                    required
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    placeholder="Enter your admin email"
                  />
                  <User className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>
              
              {/* Success Message */}
              {resetEmailSent && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                  Password reset link has been sent to your email address.
                </div>
              )}
              
              {/* Submit Button */}
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setForgotPasswordMode(false)}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  Back to Login
                </button>
                <button
                  type="submit"
                  disabled={loading || !forgotPasswordEmail}
                  className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <span>Send Reset Link</span>
                    </div>
                  )}
                </button>
              </div>
            </form>
          ) : isSignUpMode ? (
            <form className="space-y-6" onSubmit={handleSignUp}>
              {/* First Name */}
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <div className="mt-1 relative">
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={signUpData.firstName}
                    onChange={handleSignUpChange}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    placeholder="Enter your first name"
                  />
                  <User className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <div className="mt-1">
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={signUpData.lastName}
                    onChange={handleSignUpChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1 relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={signUpData.email}
                    onChange={handleSignUpChange}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    placeholder="Enter your email"
                  />
                  <User className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="signUpPassword" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="signUpPassword"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={signUpData.password}
                    onChange={handleSignUpChange}
                    className="appearance-none block w-full px-3 py-2 pl-10 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    placeholder="Create a password"
                  />
                  <Lock className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={signUpData.confirmPassword}
                    onChange={handleSignUpChange}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    placeholder="Confirm your password"
                  />
                  <Lock className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating account...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <User className="h-4 w-4 mr-2" />
                      Create Admin Account
                    </div>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <form className="space-y-6" onSubmit={handleLogin}>
            {/* User ID */}
            <div>
              <label htmlFor="userId" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <div className="mt-1 relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                  placeholder="Enter your email address"
                />
                <User className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 pl-10 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                  placeholder="Enter your password"
                />
                <Lock className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  name="rememberMe"
                  type="checkbox"
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-900">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <button 
                  type="button"
                  onClick={() => setForgotPasswordMode(true)}
                  className="font-medium text-orange-600 hover:text-orange-500 bg-transparent border-none p-0 cursor-pointer"
                >
                  Forgot your password?
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign in to Admin Panel
                  </div>
                )}
              </button>
            </div>
            </form>
          )}

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Security Notice</span>
              </div>
            </div>
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                This area is restricted to authorized personnel only. Unauthorized access attempts are monitored and may be reported to authorities.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;